/**
 * ===== assets/section-promo-popup.js — TTP Leathers Design System v1.0 =====
 * Native Vanilla ES6 Custom Element for the Promotional & Newsletter Popup.
 * Handles triggers (delay, scroll, exit intent), localStorage persistence,
 * accessibility focus trapping, body scroll locking, and Shopify Customizer events.
 */

class PromoPopup extends HTMLElement {
  constructor() {
    super();

    // 1. Core Settings & Attributes
    this.sectionId = this.getAttribute('data-section-id');
    this.expiryDays = parseFloat(this.getAttribute('data-expire-days')) || 7;
    this.triggerType = this.getAttribute('data-trigger-type') || 'delay'; // 'delay', 'scroll', 'exit_intent'
    this.delayMs = (parseFloat(this.getAttribute('data-trigger-delay')) || 5) * 1000;
    this.scrollThreshold = (parseFloat(this.getAttribute('data-trigger-scroll')) || 50) / 100;
    this.isTestMode = this.getAttribute('data-test-mode') === 'true';
    this.showOnlyHome = this.getAttribute('data-show-only-home') === 'true';
    this.showOnlyGuest = this.getAttribute('data-show-only-guest') === 'true';

    // 2. Storage Key
    this.storageKey = `bh-promo-popup-dismissed-${this.sectionId}`;

    // 3. UI Elements
    this.overlay = this.querySelector('.bh-promo-popup-overlay');
    this.container = this.querySelector('.bh-promo-popup-container');
    this.closeBtn = this.querySelector('.bh-promo-popup-close');
    this.form = this.querySelector('form');

    // 4. Bindings
    this.handleScroll = this.handleScroll.bind(this);
    this.handleExitIntent = this.handleExitIntent.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.close = this.close.bind(this);

    // 5. Initialize Listeners
    if (this.closeBtn) this.closeBtn.addEventListener('click', this.close);
    if (this.overlay) this.overlay.addEventListener('click', this.close);

    // 6. Shopify Customizer Theme Editor Integration
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', (e) => {
        if (e.detail.sectionId === this.sectionId) {
          this.open();
        }
      });
      document.addEventListener('shopify:section:deselect', (e) => {
        if (e.detail.sectionId === this.sectionId) {
          this.close(null, false); // don't persist dismissal when deselecting in editor
        }
      });
    }
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  init() {
    // Check constraints: page type and guest-only
    if (this.showOnlyHome && window.location.pathname !== Shopify.routes.root) {
      return;
    }
    if (this.showOnlyGuest && window.theme && window.theme.customerLoggedIn) {
      // Note: If theme.customerLoggedIn is not available globally, we handle fallback or check document body classes
      const bodyHasCustomerClass = document.body.classList.contains('customer-logged-in');
      if (bodyHasCustomerClass) return;
    }

    // Check Shopify form redirect/success first
    const hasSuccessMessage = this.querySelector('.form-status--success, .newsletter-form__message--success, [id^="Newsletter-success-"], .bh-promo-popup-success');
    const hasUrlSuccess = window.location.search.includes('customer_posted=true') || window.location.hash.includes('contact_form');

    if (hasSuccessMessage || (hasUrlSuccess && this.form)) {
      // If user just subscribed, show the popup with success message immediately and persist dismissal
      this.open();
      this.persistDismissal();
      return;
    }

    // Check localStorage dismissal state (if not in Test Mode or Theme Editor)
    if (!this.isTestMode && !Shopify.designMode) {
      const dismissedUntil = localStorage.getItem(this.storageKey);
      if (dismissedUntil && Date.now() < parseInt(dismissedUntil, 10)) {
        return;
      }
    }

    // Initialize triggers
    this.setupTrigger();
  }

  setupTrigger() {
    if (this.triggerType === 'delay') {
      this.triggerTimeout = setTimeout(() => this.open(), this.delayMs);
    } else if (this.triggerType === 'scroll') {
      window.addEventListener('scroll', this.handleScroll, { passive: true });
    } else if (this.triggerType === 'exit_intent') {
      // Desktop: cursor leaves the top viewport
      document.addEventListener('mouseleave', this.handleExitIntent);
      
      // Mobile fallback: since mouseleave is unavailable, trigger on rapid scroll up or scroll depth
      this.lastScrollY = window.scrollY;
      this.mobileExitTimer = setTimeout(() => this.open(), 15000); // fallback after 15s
      window.addEventListener('scroll', this.handleScroll, { passive: true });
    }
  }

  removeEventListeners() {
    if (this.triggerTimeout) clearTimeout(this.triggerTimeout);
    if (this.mobileExitTimer) clearTimeout(this.mobileExitTimer);
    window.removeEventListener('scroll', this.handleScroll);
    document.removeEventListener('mouseleave', this.handleExitIntent);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  handleScroll() {
    if (this.triggerType === 'scroll') {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalHeight <= 0) return;
      const scrollPercentage = window.scrollY / totalHeight;

      if (scrollPercentage >= this.scrollThreshold) {
        this.open();
        window.removeEventListener('scroll', this.handleScroll);
      }
    } else if (this.triggerType === 'exit_intent') {
      // Mobile exit intent: detect scroll up velocity
      const currentScrollY = window.scrollY;
      const scrollDiff = this.lastScrollY - currentScrollY;

      if (scrollDiff > 50 && currentScrollY > 150) { // rapid scroll up of 50px
        this.open();
        window.removeEventListener('scroll', this.handleScroll);
      }
      this.lastScrollY = currentScrollY;
    }
  }

  handleExitIntent(e) {
    // Triggers when cursor leaves top of screen (clientY < 20)
    if (e.clientY < 20) {
      this.open();
      document.removeEventListener('mouseleave', this.handleExitIntent);
    }
  }

  open() {
    this.removeEventListeners(); // Stop any pending triggers

    // Set open attributes
    this.setAttribute('open', 'true');
    this.classList.add('is-active');
    document.body.classList.add('bh-popup-open');

    // Accessibility: ESC key close listener
    document.addEventListener('keydown', this.handleKeydown);

    // Accessibility: Trap focus
    this.trapFocus();
  }

  close(event = null, shouldPersist = true) {
    if (event) {
      event.preventDefault();
    }

    this.removeAttribute('open');
    this.classList.remove('is-active');
    document.body.classList.remove('bh-popup-open');

    document.removeEventListener('keydown', this.handleKeydown);

    // Restore focus to body or last active element
    if (this.lastActiveElement) {
      this.lastActiveElement.focus();
    }

    if (shouldPersist && !this.isTestMode && !Shopify.designMode) {
      this.persistDismissal();
    }
  }

  persistDismissal() {
    const expireTime = Date.now() + this.expiryDays * 24 * 60 * 60 * 1000;
    localStorage.setItem(this.storageKey, expireTime.toString());
  }

  // Accessibility: Focus Trapping Logic
  trapFocus() {
    this.lastActiveElement = document.activeElement;
    
    // Find focusable elements
    const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusableElements = Array.from(this.querySelectorAll(focusableSelectors));
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Focus on first input if exists, or close button
    const firstInput = this.querySelector('input');
    if (firstInput) {
      firstInput.focus();
    } else {
      firstElement.focus();
    }

    this.focusableElements = focusableElements;
    this.firstFocusable = firstElement;
    this.lastFocusable = lastElement;
  }

  handleKeydown(e) {
    if (e.key === 'Escape') {
      this.close();
      return;
    }

    if (e.key === 'Tab' && this.focusableElements) {
      const activeEl = document.activeElement;
      if (e.shiftKey) { // Shift + Tab: backward wrap
        if (activeEl === this.firstFocusable) {
          this.lastFocusable.focus();
          e.preventDefault();
        }
      } else { // Tab: forward wrap
        if (activeEl === this.lastFocusable) {
          this.firstFocusable.focus();
          e.preventDefault();
        }
      }
    }
  }
}

// Define the custom element
if (!customElements.get('promo-popup')) {
  customElements.define('promo-popup', PromoPopup);
}
